import React, { SyntheticEvent, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useNavigate } from 'react-router-dom';
import { PageHeader, PasswordField, useNotify } from '@routes-plan/ui';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, FormActions, FormSection, apiError } from '../shared/AdminUi';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Field = 'firstName' | 'lastName' | 'mobilePhone' | 'username' | 'password' | 'passwordConfirm';

/** Crear representante: datos, contacto y acceso a la app. La ruta se asigna después desde la lista. */
const SalesRepNew: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { canCreate } = useResourcePermissions('/admin/sales-reps');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [username, setUsername] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);

  setPageTitle('salesReps.newTitle');

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const req = t('common.required');
    const found: Partial<Record<Field, string>> = {};
    if (!firstName.trim()) found.firstName = req;
    if (!lastName.trim()) found.lastName = req;
    if (!mobilePhone.trim()) found.mobilePhone = req;
    if (!password) found.password = req;
    if (!passwordConfirm) found.passwordConfirm = req;
    else if (password !== passwordConfirm) found.passwordConfirm = t('salesReps.passwordMismatch');
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(found.passwordConfirm && Object.keys(found).length === 1 ? t('salesReps.passwordMismatch') : t('salesReps.missingFields'));
      return;
    }

    setSaving(true);
    try {
      // 1. El usuario no puede estar repetido.
      const checkUsernameResponse = await axios.get(`users/check-username/${username}`);
      if (checkUsernameResponse.data.exists) {
        setErrors({ username: t('salesReps.usernameTaken') });
        notify.warning(t('salesReps.usernameTaken'));
        return;
      }
      // 2. Crear el representante.
      await axios.post('sales-reps', {
        firstName,
        lastName,
        idNumber,
        username,
        email: '',
        password,
        passwordConfirm,
        phone,
        mobilePhone,
        address: '',
        userTypeId: 1,
      });
      notify.success(t('salesReps.createdOk'));
      navigate('/admin/sales-reps');
    } catch (error) {
      notify.error(apiError(error, t('salesReps.createError')));
    } finally {
      setSaving(false);
    }
  };

  const err = (f: Field) => ({ error: !!errors[f], helperText: errors[f] });
  return (
    <AdminPage narrow>
      <PageHeader
        title={t('salesReps.newTitle')}
        description={t('salesReps.newSubtitle')}
        breadcrumbs={[{ label: t('salesReps.title'), href: '#/admin/sales-reps' }, { label: t('salesReps.newTitle') }]}
      />
      <Box component="form" noValidate onSubmit={submit} autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Campos ocultos para que el navegador no autocomplete usuario y contraseña. */}
        <input type="text" name="fake_user" style={{ display: 'none' }} autoComplete="username" />
        <input type="password" name="fake_pass" style={{ display: 'none' }} autoComplete="current-password" />
        <FormSection title={t('salesReps.sectionPerson')}>
          <TextField id="rep-first-name" label={t('common.firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} inputProps={{ maxLength: 30 }} required fullWidth autoComplete="off" {...err('firstName')} />
          <TextField id="rep-last-name" label={t('common.lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} inputProps={{ maxLength: 30 }} required fullWidth autoComplete="off" {...err('lastName')} />
          <TextField
            id="rep-id-number"
            label={t('common.idNumber')}
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ''))}
            inputProps={{ maxLength: 20, inputMode: 'numeric' }}
            fullWidth
            autoComplete="off"
          />
        </FormSection>
        <FormSection title={t('salesReps.sectionContact')}>
          <TextField
            id="rep-mobile"
            type="tel"
            label={t('common.cellphone')}
            placeholder="3101234567"
            value={mobilePhone}
            onChange={(e) => setMobilePhone(e.target.value)}
            inputProps={{ maxLength: 10 }}
            required
            fullWidth
            autoComplete="off"
            {...err('mobilePhone')}
          />
          <TextField
            id="rep-phone"
            type="tel"
            label={t('common.phone')}
            placeholder="6011234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputProps={{ maxLength: 10 }}
            fullWidth
            autoComplete="off"
          />
        </FormSection>
        <FormSection title={t('salesReps.sectionAccess')} description={t('salesReps.sectionAccessHint')}>
          <TextField
            id="rep-username"
            name="new-user-field"
            label={t('common.username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            inputProps={{ maxLength: 30 }}
            fullWidth
            autoComplete="new-username"
            {...err('username')}
          />
          <Box sx={{ display: { xs: 'none', md: 'block' } }} />
          <PasswordField
            id="rep-password"
            name="new-pass-field"
            label={t('common.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ maxLength: 30, autoComplete: 'new-password' }}
            required
            fullWidth
            {...err('password')}
          />
          <PasswordField
            id="rep-password-confirm"
            name="confirm-pass-field"
            label={t('salesReps.confirmPassword')}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            inputProps={{ maxLength: 30, autoComplete: 'new-password' }}
            required
            fullWidth
            {...err('passwordConfirm')}
          />
        </FormSection>
        <FormActions cancelTo="/admin/sales-reps" saving={saving} allowed={canCreate} deniedTooltip={t('common.noPermissionCreate')} />
      </Box>
    </AdminPage>
  );
};

export default SalesRepNew;
